import { useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/apiClient";
import { Header, Footer } from "../components/Layout";
import { CommentCaMarche } from "../components/CommentCaMarche";
import { Trophy, Zap, Shield, Users, Sparkles, Scale, Timer, TrendingUp, ExternalLink } from "lucide-react";

export default function Concours() {
  const voteUrl = process.env.REACT_APP_VOTE_URL || "https://emergent.sh/vote";

  // Track visit + vote click
  useEffect(() => {
    api.post("/analytics/track", {
      event: "visit_concours",
      referrer: document.referrer || null,
      utm: new URLSearchParams(window.location.search).get("utm_source"),
    }).catch(() => {});
  }, []);

  const trackVote = () => {
    api.post("/analytics/track", { event: "click_vote" }).catch(() => {});
  };

  const share = () => {
    const url = window.location.origin + "/concours";
    if (navigator.share) navigator.share({ title: "JurisFlash — Concours Emergent", url });
    else { navigator.clipboard.writeText(url); alert("Lien copié : " + url); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-12 pb-8">
        <div className="j-label mb-3 flex items-center gap-2"><Trophy className="w-4 h-4 text-[#FFD35A]" /> Concours Mondial Emergent · Écosystème Google Labs</div>
        <h1 className="j-h1 font-heading" data-testid="concours-title">
          JurisFlash : la justice française <span className="text-[#10B981]">accessible en 47 secondes.</span>
        </h1>
        <p className="mt-6 text-lg text-slate-600 max-w-3xl">
          Un moteur d'analyse juridique dual-face (citoyen ⚪ / avocat pro ⚫) qui rend le droit lisible, actionnable et gratuit
          pour les 12 millions de Français éligibles à l'Aide Juridictionnelle.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <a href={voteUrl} target="_blank" rel="noopener noreferrer" onClick={trackVote} className="btn-emerald text-lg" data-testid="concours-vote-btn">
            <Trophy className="w-5 h-5" /> Voter pour JurisFlash
          </a>
          <Link to="/" className="btn-navy" data-testid="concours-try-btn">
            <Zap className="w-4 h-4" /> Tester en 47 secondes
          </Link>
          <button onClick={share} className="btn-ghost" data-testid="concours-share-btn">
            <ExternalLink className="w-4 h-4" /> Partager le lien
          </button>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { n: "68 %", label: "des Français renoncent à un droit faute d'accès juridique" },
            { n: "1 200 €", label: "coût moyen d'une consultation + procédure" },
            { n: "12 M", label: "de Français éligibles à l'AJ (mais 40 % ne le savent pas)" },
          ].map((s) => (
            <div key={s.n} className="j-card">
              <div className="font-heading font-black text-4xl text-[#EF4444]">{s.n}</div>
              <div className="text-sm text-slate-600 mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* COMMENT ÇA MARCHE */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 border-t border-slate-200">
        <CommentCaMarche mode="citizen" />
      </section>

      {/* SOLUTION */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 border-t border-slate-200">
        <div className="j-label mb-2">Notre solution</div>
        <h2 className="j-h2 font-heading mb-6">Un moteur de justice cadré par 3 boucliers</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: <Shield />, title: "Juris-Guardian", desc: "Anonymisation RGPD locale, anti-hallucination Legifrance, cache offline PWA. Zero-Data Retention." },
            { icon: <Sparkles />, title: "Contrôle de complétude documentaire", desc: "Chronologie des faits, pointage des pièces manquantes et rattachement aux articles officiels Légifrance en moins de 47 secondes." },
            { icon: <Scale />, title: "Dual-Face", desc: "Espace Citoyen (gratuit, 47s) + Espace Pro B2B (49 €/mois, gestionnaires & syndics). Une seule app, deux flux métier." },
          ].map((f) => (
            <div key={f.title} className="j-card">
              <div className="w-9 h-9 rounded-lg bg-[#10B981]/10 text-[#10B981] flex items-center justify-center mb-3">{f.icon}</div>
              <h3 className="font-heading font-black text-lg text-[#1E293B]">{f.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* METRICS */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 border-t border-slate-200">
        <div className="j-label mb-2">Traction technique</div>
        <h2 className="j-h2 font-heading mb-6">Ce qui est livré, aujourd'hui</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: <Timer />, n: "47s", label: "Temps moyen d'analyse" },
            { icon: <Zap />, n: "4h30", label: "Économisées par dossier pro" },
            { icon: <Shield />, n: "100%", label: "Sources publiques officielles Légifrance & Judilibre" },
            { icon: <TrendingUp />, n: "0", label: "Donnée vendue ou partagée sans consentement" },
          ].map((s) => (
            <div key={s.label} className="j-card">
              <div className="j-label flex items-center gap-2 mb-2">{s.icon} <span>Livré</span></div>
              <div className="font-heading font-black text-3xl text-[#10B981]">{s.n}</div>
              <div className="text-xs text-slate-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW TO VOTE */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
        <div className="j-card border-[#FFD35A]/40 bg-gradient-to-br from-white to-amber-50/40">
          <div className="j-label mb-2 flex items-center gap-2"><Trophy className="w-3 h-3 text-[#FFD35A]" /> Comment voter</div>
          <h2 className="font-heading font-black text-2xl text-[#1E293B]">Un clic, une signature Google, un vote.</h2>
          <ol className="mt-4 space-y-2 text-sm text-slate-700 list-decimal pl-4">
            <li>Cliquez sur "Voter pour JurisFlash" ci-dessous</li>
            <li>Connectez-vous avec votre compte Google (aucune donnée collectée par nous)</li>
            <li>Confirmez votre vote dans la plateforme du concours Emergent</li>
          </ol>
          <a href={voteUrl} target="_blank" rel="noopener noreferrer" onClick={trackVote} className="btn-emerald w-full mt-6 text-lg" data-testid="concours-final-vote-btn">
            <Trophy className="w-5 h-5" /> Voter pour JurisFlash — 1 clic
          </a>
          <div className="text-xs text-slate-400 mt-3 text-center">
            Concours Mondial Emergent · Écosystème Google Labs · 2026
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
